import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import { ItemCustomizationStep } from "@/components/ItemCustomizationStep";
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, Clock, MapPin, Settings } from "lucide-react";
import { toast } from "sonner";

interface TemplateItem {
  id: string;
  name: string;
  type: string;
  description?: string;
  category?: string;
  min_value?: number;
  max_value?: number;
  unit?: string;
  required?: boolean;
}

interface DbTemplate {
  id: string;
  name: string;
  description?: string | null;
  frequency?: string | null;
  items: any; // Json from database
}

interface Template {
  id: string;
  name: string;
  description?: string;
  frequency: string;
  items: TemplateItem[];
}

interface Unit {
  id: string;
  name: string;
  address?: string;
}

type Step = 'template' | 'customize' | 'unit' | 'summary';

const NewChecklist = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('template');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedItems, setSelectedItems] = useState<TemplateItem[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;

    try {
      const [templatesResult, unitsResult] = await Promise.all([
        supabase
          .from('checklist_templates')
          .select('*')
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('units')
          .select('*')
          .order('name')
      ]);

      if (templatesResult.error) throw templatesResult.error;
      if (unitsResult.error) throw unitsResult.error;

      // Transform the data to match our interface
      const transformedTemplates: Template[] = (templatesResult.data || []).map((template: any) => ({
        id: template.id,
        name: template.name || '',
        description: template.description || undefined,
        frequency: template.frequency || 'daily',
        items: Array.isArray(template.items) ? template.items as TemplateItem[] : []
      }));
      
      setTemplates(transformedTemplates);
      setUnits(unitsResult.data || []);
      
      // Auto-select first unit if only one exists
      if (unitsResult.data?.length === 1) {
        setSelectedUnit(unitsResult.data[0]);
      }
    } catch (error: any) {
      toast.error("Error loading data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const createChecklist = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a template");
      return;
    }

    if (!selectedUnit) {
      toast.error("Please select a unit");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to create a checklist");
      return;
    }

    if (selectedItems.length === 0) {
      toast.error("Please select at least one item");
      return;
    }

    setCreating(true);

    try {
      const { data, error } = await supabase
        .from('checklists')
        .insert({
          template_id: selectedTemplate.id,
          unit_id: selectedUnit.id,
          scheduled_for: new Date().toISOString(),
          status: 'pending',
          assigned_to: user.id,
          selected_items: selectedItems as any
        })
        .select()
        .single();

      if (error) throw error;

      toast.success("Checklist created successfully!");
      navigate(`/checklist-execution/${data.id}`);
    } catch (error) {
      console.error('Error creating checklist:', error);
      toast.error("Failed to create checklist. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  const getStepNumber = (step: Step): number => {
    switch (step) {
      case 'template': return 1;
      case 'customize': return 2;
      case 'unit': return 3;
      case 'summary': return 4;
    }
  };

  const getStepIcon = (step: Step) => {
    switch (step) {
      case 'template': return <Settings className="h-4 w-4" />;
      case 'customize': return <CheckCircle2 className="h-4 w-4" />;
      case 'unit': return <MapPin className="h-4 w-4" />;
      case 'summary': return <Clock className="h-4 w-4" />;
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setCurrentStep('customize');
  };

  const handleItemsSelected = (items: TemplateItem[]) => {
    setSelectedItems(items);
    setCurrentStep('unit');
  };

  const handleUnitSelect = (unit: Unit) => {
    setSelectedUnit(unit);
    setCurrentStep('summary');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  const currentStepNumber = getStepNumber(currentStep);
  const totalSteps = 4;
  const progressPercentage = (currentStepNumber / totalSteps) * 100;

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard')}
        className="mb-6 -ml-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Header with Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold">Create New Checklist</h1>
            <p className="text-muted-foreground">Step {currentStepNumber} of {totalSteps}</p>
          </div>
          
          <div className="flex items-center gap-2">
            {(['template', 'customize', 'unit', 'summary'] as Step[]).map((step, index) => {
              const stepNumber = index + 1;
              const isActive = step === currentStep;
              const isCompleted = stepNumber < currentStepNumber;
              
              return (
                <div key={step} className="flex items-center">
                  <div className={`
                    flex items-center justify-center w-8 h-8 rounded-full border-2 
                    ${isCompleted ? 'bg-primary border-primary text-primary-foreground' :
                      isActive ? 'border-primary text-primary' : 'border-muted text-muted-foreground'}
                  `}>
                    {isCompleted ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      getStepIcon(step)
                    )}
                  </div>
                  
                  {index < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${
                      stepNumber < currentStepNumber ? 'bg-primary' : 'bg-muted'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        <Progress value={progressPercentage} className="h-2" />
      </div>

      {/* Step Content */}
      <div className="space-y-6">
        {currentStep === 'template' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Select Template
              </CardTitle>
              <CardDescription>Choose a checklist template to get started</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <Card 
                    key={template.id} 
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2">{template.name}</h3>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                      )}
                      <div className="flex gap-2">
                        {template.frequency && (
                          <Badge variant="secondary">
                            {template.frequency}
                          </Badge>
                        )}
                        <Badge variant="outline">
                          {template.items.length} items
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {currentStep === 'customize' && selectedTemplate && (
          <ItemCustomizationStep
            template={selectedTemplate}
            onNext={handleItemsSelected}
            onBack={() => setCurrentStep('template')}
          />
        )}

        {currentStep === 'unit' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Select Location
              </CardTitle>
              <CardDescription>Choose where this checklist will be performed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {units.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="h-16 w-16 mx-auto text-muted-foreground mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">No Locations Found</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    You need to create at least one location (site) before creating checklists.
                    Locations represent physical places where checklists will be performed.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setCurrentStep('customize')}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                    <Button onClick={() => navigate('/units')}>
                      <MapPin className="mr-2 h-4 w-4" />
                      Go to Sites
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {units.map((unit) => (
                      <Card 
                        key={unit.id} 
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleUnitSelect(unit)}
                      >
                        <CardContent className="p-4">
                          <h3 className="font-semibold mb-1">{unit.name}</h3>
                          {unit.address && (
                            <p className="text-sm text-muted-foreground">{unit.address}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="flex justify-between pt-4">
                    <Button variant="outline" onClick={() => setCurrentStep('customize')}>
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {currentStep === 'summary' && selectedTemplate && selectedUnit && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Summary & Create
              </CardTitle>
              <CardDescription>Review your selections and create the checklist</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-muted rounded-lg space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold">Template</h3>
                    <p className="text-sm text-muted-foreground">{selectedTemplate.name}</p>
                  </div>
                  <Badge variant="secondary">{selectedTemplate.frequency}</Badge>
                </div>
                
                <div>
                  <h3 className="font-semibold">Location</h3>
                  <p className="text-sm text-muted-foreground">{selectedUnit.name}</p>
                  {selectedUnit.address && (
                    <p className="text-xs text-muted-foreground">{selectedUnit.address}</p>
                  )}
                </div>
                
                <div>
                  <h3 className="font-semibold">Selected Items</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedItems.length} items selected for inspection
                  </p>
                </div>
              </div>
              
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep('unit')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                <Button 
                  onClick={createChecklist} 
                  disabled={creating}
                  className="min-w-[140px]"
                >
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Checklist
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default NewChecklist;